import React, { useId } from 'react';

export interface FormSectionProps {
  title: string;
  description?: string;
  children: React.ReactNode;
  collapsible?: boolean;
  defaultExpanded?: boolean;
  badge?: string;
  icon?: React.ReactNode;
}

export const FormSection: React.FC<FormSectionProps> = ({
  title,
  description,
  children,
  collapsible = false,
  defaultExpanded = true,
  badge,
  icon,
}) => {
  const [isExpanded, setIsExpanded] = React.useState(defaultExpanded);
  const titleId = useId();

  const toggleExpanded = () => {
    if (collapsible) {
      setIsExpanded(!isExpanded);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (collapsible && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      toggleExpanded();
    }
  };

  return (
    <section aria-labelledby={titleId} className="bg-white rounded-lg border border-gray-200 overflow-hidden mb-6">
      <div
        className={`
          px-6 py-4 bg-gray-50 border-b border-gray-200
          ${collapsible ? 'cursor-pointer hover:bg-gray-100 transition-colors' : ''}
        `}
        onClick={toggleExpanded}
        onKeyDown={handleKeyDown}
        role={collapsible ? 'button' : undefined}
        tabIndex={collapsible ? 0 : undefined}
        aria-expanded={collapsible ? isExpanded : undefined}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            {icon && <span className="text-green-600">{icon}</span>}
            <div>
              <h3 id={titleId} className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                {title}
                {badge && (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    {badge}
                  </span>
                )}
              </h3>
              {description && <p className="text-sm text-gray-500 mt-0.5">{description}</p>}
            </div>
          </div>

          {collapsible && (
            <svg
              className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          )}
        </div>
      </div>

      {(!collapsible || isExpanded) && <div className="px-6 py-6">{children}</div>}
    </section>
  );
};

export default FormSection;
